import React, { useCallback, useEffect, useRef, useState } from "react";
import Images from "assets/images";
import Header from "components/header/Header";
import BottomFixedButton from "components/bottomFixedButton/BottomFixedButton";
import Spacer from "components/Spacer/Spacer";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import { usePrimaryCareVisitFlow } from "hooks/useVisitFlow";
import DoctorCard from "components/doctorCard/DoctorCard";
import Accordion from "components/accordion/Accordion";
import { useApiQuery } from "hooks/useApiQuery";
import { getGeoDataFromZipcodeAction, userProfileAction } from "api/actions/UserActions";
import {
  bookSlotAction,
  discardVisit,
  getBookingSlotsWithProvidersAction,
  getRevisedInsuranceAction,
  visitConcerns,
} from "api/actions/VisitActions";

import useUpdatePrimaryCareVisit from "hooks/visit/useUpdatePrimaryCareVisit";
import { primaryCareVisitFlowReducers } from "reducers/primary_care/primaryCareVisitFlowReducer";
import { useDispatch } from "react-redux";
import {
  currentSpeciality,
  setUserDetails,
  updateVisitMessagePayload,
} from "reducers/currentUserReducer";
import { primary_care_sub_steps, speciality } from "helpers/enum";
import { useHistory } from "react-router-dom";
import { useCurrentUser } from "hooks/useCurrentUser";
import usePatients from "hooks/visit/usePatients";
import { CURRENT_SOURCE } from "helpers/currentSource";
import useIncompleteData from "hooks/visit/useIncompleteData";
import RevisedPaymentBlock from "../../visitBlock/RevisedPaymentBlock";
import { isMobile } from "react-device-detect";
import useCreatePrimaryCareVisit from "hooks/visit/useCreatePrimaryCareVisit";
import useModal from "hooks/useModal";
import Alert from "components/alerts/Alert";
import { useApiMutation } from "hooks/useApiMutation";
import useToastify from "hooks/useToastify";
import LoaderComponent from "components/loader/LoaderComponent";
import { isDesktop } from "react-device-detect";
import useTextCompleteVisitToMobile from "hooks/useTextCompleteVisitToMobile";

import SelectPatient from "./phaseTwoSections/SelectPatient";
import SelectCondition from "./phaseTwoSections/SelectCondition";
import SelectSlot from "./phaseTwoSections/SelectSlot";
import BasicDetails from "./phaseTwoSections/BasicDetails";
import IdentityConfirmation from "./phaseTwoSections/IdentityConfirmation";
import SelectPharmacy from "./phaseTwoSections/SelectPharmacy";
import Symptoms from "./phaseTwoSections/Symptoms";
import Allergies from "./phaseTwoSections/Allergies";
import SocialHistory from "./phaseTwoSections/SocialHistory";
import Medications from "./phaseTwoSections/Medications";
import FemaleMedicalHistory from "./phaseTwoSections/FemaleMedicalHistory";
import AdditionalInfo from "./phaseTwoSections/AdditionalInfo";
import ReferralSource from "./phaseTwoSections/ReferralSource";
import CodeOfConduct from "./phaseTwoSections/CodeOfConduct";
import PaymentDetails from "./phaseTwoSections/PaymentDetails";

// Upload Image Failure UI
import { IoAlertCircleOutline } from "react-icons/io5";
import startCase from "lodash.startcase";
import isEqual from "lodash.isequal";
import { useParameterizedQuery } from "react-fetching-library";

import { trackEvent } from "helpers/analytics";
import Button from "components/buttons/Button";
import {
  imageUploadErrorReport,
  successImageUploadReport,
} from "api/actions/ImageUploadActions";
import useImageUpload from "hooks/visit/useImageUpload";

function PrimaryCareCompleteVisitDetails() {
  const { setShowModal } = useModal();
  const { incompleteData } = usePrimaryCareVisitFlow();
  const [activeIndex, setActiveIndex] = useState(0);
  const [disabledIndices, setDisabledIndices] = useState([]);
  const [currentPatient, setCurrentPatient] = useState({});
  const [selectedPharmacy, setSelectedPharmacy] = useState(
    incompleteData?.pharmacy_details || {}
  );
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [shareAllergy, setShareAllergy] = useState(null);
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [shareMeds, setShareMeds] = useState(null);
  const [selectedMedications, setSelectedMedications] = useState([]);

  const [firstAnswer, setFirstAnswer] = useState("");
  const [secondAnswer, setSecondAnswer] = useState("");
  const [thirdAnswer, setThirdAnswer] = useState("");
  const [thirdYesAnswer, setThirdYesAnswer] = useState([]);
  const [fourthAnswer, setFourthAnswer] = useState("");
  const [fourthYesAnswer, setFourthYesAnswer] = useState([]);
  const [imageData, setImageData] = useState(null);
  const [updatedZip, setUpdatedZip] = useState("");

  const { fetchIncompleteData } = useIncompleteData();

  const firstTime = useRef(true);
  const updateCurrentPatient = useRef(false);
  const updateMedications = useRef(false);
  const updateAllergies = useRef(false);
  const trialref = useRef(null);
  const idRef = useRef(true);
  const imagePickerRef = useRef(null);
  const {
    payload: profileDetails,
    query: getProfileDetails,
    loading: gettingProfileDetails,
  } = useApiQuery(userProfileAction, false);
  const { alertPatient } = useTextCompleteVisitToMobile();

  const {
    payload: revisedInsuranceDetails,
    query: getRevisedInsuranceDetails,
  } = useApiQuery(getRevisedInsuranceAction(incompleteData?.id), false);
  const { mutate: callDiscardVisit, loading: discardingVisit } =
    useApiMutation(discardVisit);
  // Network check removed per new requirement — do not call check-insurance API.
  const checkingInsuranceInNetwork = false;
  const insuranceInNetwork = null;

  const {
    query: getBookingSlots,
    loading: gettingBookingSlots,
    payload: slotsResponse,
  } = useApiQuery(
    getBookingSlotsWithProvidersAction({
      caseId: incompleteData?.id,
    }),
    false
  );

  const {
    payload: visitConcernsPayload,
    loading: gettingConcerns,
    query: queryConcerns,
  } = useApiQuery(visitConcerns(2), false);

  const { payload: zipDetails, query: getZipcodeDetails } = useApiQuery(
    getGeoDataFromZipcodeAction(updatedZip),
    false
  );

  const { createNewVisit, creatingVisit } = useCreatePrimaryCareVisit();
  const { updateVisit, updatingVisit } = useUpdatePrimaryCareVisit();
  const dispatch = useDispatch();
  const history = useHistory();
  const { showAlert } = useToastify();
  const { userDetails } = useCurrentUser();
  const {
    patientsList,
    getPatients,
    getCurrentPatient,
    fetchingPatients,
  } = usePatients();
  const { loading: bookingSlot, mutate: callBookSlot } = useApiMutation(
    bookSlotAction
  );
  const [shouldShowReferralSourceStep, setShouldShowReferralSourceStep] =
    useState(
      userDetails?.referral_source === null ||
        userDetails?.referral_source_saved_type === "temporary"
    );

  const referralSourceVisitIdRef = useRef(null);
  const pcReferralPanelLeftRef = useRef(false);

  useEffect(() => {
    if (
      incompleteData?.id &&
      referralSourceVisitIdRef.current !== incompleteData?.id
    ) {
      referralSourceVisitIdRef.current = incompleteData?.id;
      pcReferralPanelLeftRef.current = false;
      setShouldShowReferralSourceStep(
        userDetails?.referral_source === null ||
          userDetails?.referral_source_saved_type === "temporary"
      );
    }
  }, [
    incompleteData?.id,
    userDetails?.referral_source,
    userDetails?.referral_source_saved_type,
  ]);

  const scrollToActiveSection = useCallback(() => {
    if (activeIndex >= 0 && incompleteData) {
      trialref?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [activeIndex, incompleteData]);

  useEffect(() => {
    const init = async () => {
      await fetchIncompleteData(speciality.primary_care);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      scrollToActiveSection();
    }, 500);
  }, [activeIndex, scrollToActiveSection]);

  useEffect(() => {
    if (incompleteData?.patient_id) {
      getBookingSlots();
      queryConcerns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incompleteData?.patient_id]);

  useEffect(() => {
    if (profileDetails) {
      getPatients();
    }
  }, [profileDetails, getPatients]);

  useEffect(() => {
    if (patientsList && patientsList?.length > 0) {
      if (currentPatient && Object.keys(currentPatient).length === 0) {
        updateCurrentPatient.current = true;
      } else {
        if (
          !isEqual(
            currentPatient,
            getCurrentPatient(incompleteData?.patient_id, patientsList)
          )
        ) {
          updateCurrentPatient.current = true;
        }
      }
    }
  }, [currentPatient, getCurrentPatient, incompleteData, patientsList]);

  useEffect(() => {
    if (updateCurrentPatient.current) {
      updateCurrentPatient.current = false;

      let temp = getCurrentPatient(incompleteData?.patient_id, patientsList);
      setCurrentPatient(temp);
      setUpdatedZip(temp?.zip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientsList]);

  useEffect(() => {
    if (updatedZip !== "") {
      getZipcodeDetails();
    }
  }, [updatedZip, getZipcodeDetails]);

  useEffect(() => {
    if (firstTime.current && incompleteData) {
      const steps = incompleteData?.pc_second_phase_steps ?? {};
      const isCompleted = (key) => steps?.[key] === "completed";

      let nextIndex = 0;

      if (!isCompleted(primary_care_sub_steps.select_patient)) {
        nextIndex = 0;
      } else if (!isCompleted(primary_care_sub_steps.select_condition)) {
        nextIndex = 1;
      } else if (!isCompleted(primary_care_sub_steps.select_slot)) {
        nextIndex = 2;
      } else if (!isCompleted(primary_care_sub_steps.confirm_residence)) {
        nextIndex = 3;
      } else if (!isCompleted(primary_care_sub_steps.identity_confirmation)) {
        nextIndex = 4;
      } else if (!isCompleted(primary_care_sub_steps.select_pharmacy)) {
        nextIndex = 5;
      } else if (!isCompleted(primary_care_sub_steps.symptoms)) {
        nextIndex = 6;
      } else if (!isCompleted(primary_care_sub_steps.social_history)) {
        nextIndex = 7;
      } else if (!isCompleted(primary_care_sub_steps.allergies)) {
        nextIndex = 8;
      } else if (!isCompleted(primary_care_sub_steps.medications)) {
        nextIndex = 9;
      } else if (currentPatient?.gender === "female") {
        if (!isCompleted(primary_care_sub_steps.female_medical_history)) {
          nextIndex = 10;
        } else if (!isCompleted(primary_care_sub_steps.additional_info)) {
          nextIndex = 11;
        } else if (shouldShowReferralSourceStep) {
          nextIndex = pcReferralPanelLeftRef.current ? 13 : 12;
        } else if (!incompleteData?.accepted_tnc) {
          nextIndex = 13;
        } else if (!incompleteData?.meta?.submitted_payment_info) {
          nextIndex = 14;
        } else {
          nextIndex = null;
        }
      } else {
        if (!isCompleted(primary_care_sub_steps.additional_info)) {
          nextIndex = 10;
        } else if (shouldShowReferralSourceStep) {
          nextIndex = pcReferralPanelLeftRef.current ? 12 : 11;
        } else if (!incompleteData?.accepted_tnc) {
          nextIndex = 12;
        } else if (!incompleteData?.meta?.submitted_payment_info) {
          nextIndex = 13;
        } else {
          nextIndex = null;
        }
      }

      setActiveIndex(nextIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incompleteData, currentPatient, shouldShowReferralSourceStep]);

  useEffect(() => {
    if (
      firstTime.current &&
      incompleteData?.payment_method === "insurance" &&
      incompleteData?.speciality === "primary_care" &&
      patientsList
    ) {
      firstTime.current = false;
      getRevisedInsuranceDetails();
    }
  }, [incompleteData, getRevisedInsuranceDetails, patientsList]);

  useEffect(() => {
    if (incompleteData) {
      if (Array.isArray(incompleteData?.social_or_medical_history)) {
        setFirstAnswer("");
        setSecondAnswer("");
        setThirdAnswer("");
        setThirdYesAnswer([]);
        setFourthAnswer("");
        setFourthYesAnswer([]);

        incompleteData?.social_or_medical_history?.forEach((question) => {
          switch (question?.health_profile_section) {
            case "exercise":
              setFirstAnswer(
                question?.answer?.[0] + question?.answer?.slice(1)?.toLowerCase()
              );
              break;
            case "alcohol":
              setSecondAnswer(question?.answer ?? "");
              break;
            case "tobacco":
              setThirdAnswer(
                question?.answer?.[0] +
                  question?.answer?.slice(1)?.toLowerCase()
              );
              setThirdYesAnswer(question?.form_of_tobacco ?? []);
              break;
            case "medical_history": {
              const none = question?.answer === "NO";
              setFourthAnswer(none ? "None" : "Yes");

              if (question?.selected_options?.length > 0) {
                const selectedOptions = [];
                question?.selected_options?.forEach((option) => {
                  option?.diseases?.forEach((disease) => {
                    selectedOptions.push({
                      category: option?.category,
                      disease,
                    });
                  });
                });
                setFourthYesAnswer(selectedOptions);
              } else {
                setFourthYesAnswer([]);
              }
              break;
            }
            default:
              break;
          }
        });
      }

      if (incompleteData?.allergies.length === 0) {
        setShareAllergy(
          incompleteData?.health_profile_allergies.length === 0 ? "None" : null
        );
        setSelectedAllergies(incompleteData?.health_profile_allergies);
      } else {
        if (incompleteData?.allergies.includes("None")) {
          setShareAllergy(
            incompleteData?.health_profile_allergies.length === 0
              ? "None"
              : null
          );
          setSelectedAllergies(incompleteData?.health_profile_allergies);
        } else {
          setShareAllergy(null);
          setSelectedAllergies(incompleteData?.allergies_json);
        }
      }

      if (incompleteData?.medications.length === 0) {
        setShareMeds("None");
        setSelectedMedications([]);
      } else {
        if (incompleteData?.medications.includes("None")) {
          setShareMeds("None");
          setSelectedMedications([]);
        } else {
          setShareMeds(null);
          setSelectedMedications(
            incompleteData?.medications?.map((med) => {
              return {
                ...med?.details,
                name: med?.name,
              };
            })
          );
        }
      }
    }

    if (
      !incompleteData?.images_uploaded &&
      incompleteData?.meta?.submitted_payment_info
    ) {
      failedImagesRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [incompleteData]);

  useEffect(() => {
    if (
      currentPatient?.medications?.length > 0 &&
      incompleteData?.medications?.length === 0 &&
      incompleteData?.pc_second_phase_steps?.medications !== "completed"
    ) {
      setShareMeds(null);
      updateMedications.current = true;
    }
  }, [currentPatient, incompleteData]);

  useEffect(() => {
    if (updateMedications.current === true) {
      updateMedications.current = false;
      setSelectedMedications(
        currentPatient?.medications
          ?.filter(
            (med) =>
              med?.medication_type === "non_dosespot" ||
              med?.medication_type === "self_reported_medication" ||
              med?.medication_type === "prescription"
          )
          ?.map((med) => {
            if (med?.medication_type === "non_dosespot") {
              return {
                name: med?.drug_name,
                dose: med?.details?.dose,
                frequency: med?.details?.frequency,
                per: med?.details?.per,
                form: med?.details?.form,
                other_form: med?.details?.other_form,
              };
            }

            if (med?.medication_type === "prescription") {
              return {
                name: med?.drug_name,
                dose: med?.details?.dose,
                frequency: med?.details?.frequency,
                per: med?.details?.per,
                form: med?.details?.form,
                other_form: med?.details?.other_form,
                dosespot_medication_id: med?.details?.dosespot_medication_id,
              };
            }

            return {
              name: med?.drug_name,
              DispensableDrugId: med?.details?.dispensable_drug_id,
              RoutedDoseFormDrugId: med?.details?.routed_dose_form_drug_id,
              NDC: med?.details?.ndc,
              RxCUI: med?.details?.rx_cui || null,
              Strength: med?.details?.strength || null,
              IsObsolete: med?.details?.is_obsolete,
              dose: med?.details?.dose,
              frequency: med?.details?.frequency,
              per: med?.details?.per,
              form: med?.details?.form,
              other_form: med?.other_form,
            };
          })
      );
    }
  }, [currentPatient, incompleteData]);

  useEffect(() => {
    if (
      currentPatient?.allergies?.length > 0 &&
      incompleteData?.allergies?.length === 0 &&
      incompleteData?.pc_second_phase_steps?.allergies !== "completed"
    ) {
      setShareAllergy(null);
      updateAllergies.current = true;
    }
  }, [currentPatient, incompleteData]);

  useEffect(() => {
    if (updateAllergies.current === true) {
      updateAllergies.current = false;
      setSelectedAllergies(currentPatient?.allergies);
    }
  }, [currentPatient, incompleteData]);

  useEffect(() => {
    const arr = [];
    if (incompleteData?.pc_second_phase_steps?.select_patient !== "completed") {
      arr.push(1);
    }
    if (
      incompleteData?.pc_second_phase_steps?.select_condition !== "completed"
    ) {
      arr.push(2);
    }
    if (incompleteData?.pc_second_phase_steps?.select_slot !== "completed") {
      arr.push(3);
    }
    if (
      incompleteData?.pc_second_phase_steps?.confirm_residence !== "completed"
    ) {
      arr.push(4);
    }
    if (
      incompleteData?.pc_second_phase_steps?.identity_confirmation !==
      "completed"
    ) {
      arr.push(5);
    }
    if (
      incompleteData?.pc_second_phase_steps?.select_pharmacy !== "completed"
    ) {
      arr.push(6);
    }
    if (incompleteData?.pc_second_phase_steps?.symptoms !== "completed") {
      arr.push(7);
    }
    if (incompleteData?.pc_second_phase_steps?.social_history !== "completed") {
      arr.push(8);
    }
    if (incompleteData?.pc_second_phase_steps?.allergies !== "completed") {
      arr.push(9);
    }

    if (currentPatient?.gender === "female") {
      if (incompleteData?.pc_second_phase_steps?.medications !== "completed") {
        arr.push(10);
      }
      if (
        incompleteData?.pc_second_phase_steps?.female_medical_history !==
        "completed"
      ) {
        arr.push(11);
        arr.push(12);
      }
      if (!incompleteData?.accepted_tnc) {
        arr.push(13);
      }
    } else {
      if (incompleteData?.pc_second_phase_steps?.medications !== "completed") {
        arr.push(10);
        arr.push(11);
      }
      if (!incompleteData?.accepted_tnc) {
        arr.push(12);
      }
    }
    setDisabledIndices(arr);
  }, [incompleteData, currentPatient]);

  const submitVisit = async () => {
    try {
      const payload = {
        date: incompleteData?.meta?.booking_info?.selectedDate,
        slot_id: incompleteData?.meta?.booking_info?.selectedProvider?.slot_id,
        practice_user_id:
          incompleteData?.meta?.booking_info?.selectedProvider?.id,
      };
      const result = await callBookSlot({
        caseId: incompleteData?.id,
        payload,
      });
      if (result && result?.error === false) {
        updateVisit({
          payload: {
            case: {
              allergies: incompleteData?.allergies_json,
              medications: incompleteData?.medications?.map((med) => {
                return {
                  ...med?.details,
                  name: med?.name,
                };
              }),
              phase: "second_phase",
              coupon_code: incompleteData?.meta?.coupon_code,
              status: "complete_awaiting_diagnosis",
              submitting_source: CURRENT_SOURCE,
            },
          },
          change_current_screen: false,
          screen_to_complete: null,
          callBack: () => {
            dispatch(primaryCareVisitFlowReducers.reset());
            dispatch(currentSpeciality({ currentSpeciality: null }));
            dispatch(
              setUserDetails({
                userDetails: { ...userDetails, incomplete_visit_id: null },
              })
            );
            history.replace({
              pathname: "/visit/submitted",
              state: {
                speciality: speciality.primary_care,
                currentPatient,
                bookingInfo: incompleteData?.meta?.booking_info,
              },
            });
          },
        });
      } else {
        if (result.error || result.payload.error || result.payload.error_msg) {
          showAlert(
            result.payload.error ||
              result.payload.error_msg ||
              result.payload.message ||
              "Booking failed.",
            "error"
          );
        }
      }
    } catch (err) {}
  };

  const showConfirmation = () => {
    setShowModal({
      state: true,
      children: (_, onClose) => (
        <div className="flex flex-col items-center justify-center h-screen p-4">
          <div className="bg-white p-8 rounded-lg max-w-2xl m-auto w-full">
            <p className="text-2xl font-medium text-gray-500 text-center">You have currently selected <strong className="text-black">{incompleteData?.payment_method.charAt(0).toUpperCase() + incompleteData?.payment_method.slice(1)}</strong> as the mode of payment. Click on 'Proceed' to submit your visit or click 'Cancel' and go to payment selection to change the mode of payment.</p>
            <div className="flex space-x-4 mt-16 justify-center">
              <Button outlined className="h-18 text-2xl" onClick={onClose} label="Cancel" />
              <Button className="h-18 text-2xl" onClick={()=>{onClose(); submitVisit()}} label="Proceed" />
            </div>
          </div>
        </div>
      ),
      data: { preventRender: true },
    });
  }

  useEffect(() => {
    getProfileDetails();
  }, [getProfileDetails]);

  const openChat = () => {
    if (incompleteData?.messages?.length === 0) {
      return;
    } else {
      dispatch(
        updateVisitMessagePayload({
          follow_up: incompleteData?.follow_up,
          unread_provider_messages: incompleteData?.unread_provider_messages,
          messages: incompleteData?.messages,
          messagesAvatars: incompleteData?.messages_avatars,
        })
      );

      history.push(`/chat/${incompleteData?.id}`, {
        speciality: incompleteData?.speciality,
      });
    }
  };

  const alertDesktopPatient = () => {
    if (isDesktop) {
      alertPatient({
        message:
          "It looks like you're on a device that does not have camera access. You will be asked to upload your photo ID now. Do you want to use your smartphone to take photo? We'll text you a link to complete your visit on your smartphone.",
        heading: "Use your smartphone to take photos?",
        openImagePicker: () => imagePickerRef.current.click(),
      });
    } else {
      imagePickerRef.current.click();
    }
  };

  // INFO: Image upload failure UI
  const failedImagesRef = useRef(null);
  const [idImageData, setIdImageData] = useState(null);
  const [idProofLoading, setIdLoading] = useState(false);

  const { query: successImageTracker } = useParameterizedQuery(
    successImageUploadReport
  );
  const { query: reportImageUploadFailure } = useParameterizedQuery(
    imageUploadErrorReport
  );
  const { uploadImage } = useImageUpload();

  function handleIdImageChange(event) {
    const { target } = event;
    const { files } = target;

    if (files && files[0]) {
      let reader = new FileReader();
      reader.onload = (event) => {
        let file_object;
        file_object = {
          uri: files?.[0]?.name?.toString(),
          file: files[0],
          filename: files?.[0]?.name?.toString(),
          data: event?.target?.result,
          size: (files[0]?.size / 1000000).toFixed(2),
        };
        setIdImageData(file_object);
        trackEvent(`identity_image_size`, {
          size: file_object?.size + "mb",
        });
      };
      reader.readAsDataURL(files[0]);
    }
  }

  async function handleIdImageSubmit() {
    try {
      setIdLoading(true);
      const data = await uploadImage(idImageData);

      if (!data) {
        setIdLoading(false);
        showAlert("Image Upload Failed - Please try again", "error");
        setIdImageData(null);
      } else {
        await successImageTracker({
          id: incompleteData?.id,
          payload: {
            uploaded_image: "identity_confirmation",
          },
        });
        updateVisit({
          payload: {
            case: {
              allergies: incompleteData?.allergies_json,
              medications:
                typeof incompleteData?.medications[0] === "string"
                  ? incompleteData?.medications.map((item) => ({
                      name: item,
                    }))
                  : incompleteData?.medications?.map((med) => {
                      return {
                        ...med?.details,
                        name: med?.name,
                      };
                    }),
              pc_second_phase_steps: {
                ...incompleteData?.pc_second_phase_steps,
                [primary_care_sub_steps.identity_confirmation]: "completed",
              },
            },
          },
          change_current_screen: false,
          screen_to_complete: null,
          callBack: () => {
            setIdLoading(false);
            showAlert("ID Proof submitted successfully", "success");
            dispatch(
              primaryCareVisitFlowReducers.identity_confirmation({
                value: idImageData,
              })
            );
          },
        });
      }
    } catch {
      setIdLoading(false);
      showAlert("Image Upload Failed - Please try again", "error");
      setIdImageData(null);
    }
  }

  const AccordionHeader = ({
    headerTitle,
    isActive,
    stepCompleted,
    enabled,
  }) => (
    <div
      className={`flex justify-between items-start py-10 px-10 ${
        isActive ? "rounded-t-4xl" : "rounded-4xl"
      } ${enabled ? "bg-white" : "bg-lightGrey cursor-not-allowed"}`}
    >
      <h3 className="w-3/4 text-3xl font-medium text-left">{headerTitle}</h3>
      <div className="flex self-center space-x-4">
        {stepCompleted ? (
          <img
            src={Images.stepCompleted}
            alt="step_status"
            className="w-10 h-10"
          />
        ) : (
          <div />
        )}
        <img
          src={Images.dropdown}
          alt="chev_down"
          className={`transition duration-500 ease-in-out w-10 h-10 transform ${
            isActive ? "rotate-180" : "rotate-0"
          }`}
        />
      </div>
    </div>
  );

  const AccordionContent = ({ children }) => <div>{children}</div>;

  const maleAccordionData = [
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Patient"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_patient ===
            "completed"
          }
          enabled={true}
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 0 ? <div ref={trialref} /> : <div />}
          <SelectPatient
            incompleteData={incompleteData}
            fetchingPatients={fetchingPatients}
            fetchingProfileDetails={gettingProfileDetails}
            createNewVisit={createNewVisit}
            creatingVisit={creatingVisit}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Condition"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_condition ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_patient ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 1 ? <div ref={trialref} /> : <div />}
          <SelectCondition
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            selected={selectedCondition}
            setSelected={setSelectedCondition}
            gettingConcerns={gettingConcerns}
            visitConcernsPayload={
              visitConcernsPayload?.visit_converns?.map((el) => el?.title) ?? []
            }
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Date & Time Slot"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_slot === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_condition ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 2 ? <div ref={trialref} /> : <div />}
          {history?.location?.state?.timeslotId ? (
            <SelectSlot
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
              gettingBookingSlots={gettingBookingSlots}
              slotsResponse={slotsResponse}
              preselectedSlotId={history?.location?.state?.timeslotId}
            />
          ) : (
            <SelectSlot
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
              gettingBookingSlots={gettingBookingSlots}
              slotsResponse={slotsResponse}
            />
          )}
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Enter demographics"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.confirm_residence ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_slot === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 3 ? <div ref={trialref} /> : <div />}
          <BasicDetails
            incompleteData={incompleteData}
            basicDetails={currentPatient}
            refetch={getProfileDetails}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            zipDetails={zipDetails}
            setUpdatedZip={setUpdatedZip}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Confirm your identity"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.identity_confirmation ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.confirm_residence ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 4 ? <div ref={trialref} /> : <div />}
          <IdentityConfirmation
            alertDesktopPatient={alertDesktopPatient}
            firstTimeRef={idRef}
            imagePickerRef={imagePickerRef}
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            imageData={imageData}
            setImageData={setImageData}
            successImageTracker={successImageTracker}
            reportImageUploadFailure={reportImageUploadFailure}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Add your preferred pharmacy"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.select_pharmacy ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.identity_confirmation ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 5 ? <div ref={trialref} /> : <div />}
          <SelectPharmacy
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            selectedPharmacy={selectedPharmacy}
            setSelectedPharmacy={setSelectedPharmacy}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List all symptoms from the past 2 weeks"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.symptoms === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_pharmacy ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 6 ? <div ref={trialref} /> : <div />}
          <Symptoms
            basicDetails={currentPatient}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Social History"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.social_history ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.symptoms === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 7 ? <div ref={trialref} /> : <div />}
          <SocialHistory
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            currentPatient={currentPatient}
            firstAnswer={firstAnswer}
            setFirstAnswer={setFirstAnswer}
            secondAnswer={secondAnswer}
            setSecondAnswer={setSecondAnswer}
            thirdAnswer={thirdAnswer}
            setThirdAnswer={setThirdAnswer}
            thirdYesAnswer={thirdYesAnswer}
            setThirdYesAnswer={setThirdYesAnswer}
            fourthAnswer={fourthAnswer}
            setFourthAnswer={setFourthAnswer}
            fourthYesAnswer={fourthYesAnswer}
            setFourthYesAnswer={setFourthYesAnswer}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List any allergies"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.allergies === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.social_history ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 8 ? <div ref={trialref} /> : <div />}
          <Allergies
            currentPatient={currentPatient}
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            selected={shareAllergy}
            setSelected={setShareAllergy}
            selectedAllergies={selectedAllergies}
            setSelectedAllergies={setSelectedAllergies}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List any medications you are currently taking and/or review your current medication list."
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.medications === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.allergies === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 9 ? <div ref={trialref} /> : <div />}
          <Medications
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            selected={shareMeds}
            setSelected={setShareMeds}
            selectedMedications={selectedMedications}
            setSelectedMedications={setSelectedMedications}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Record/Write any additional information you would like your doctor to know"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.additional_info ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.medications === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 10 ? <div ref={trialref} /> : <div />}
          <AdditionalInfo
            userDetails={currentPatient}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    
    ...(shouldShowReferralSourceStep
      ? [
          {
            header: ({ isActive }) => (
              <AccordionHeader
                headerTitle="How did you hear about us?"
                isActive={isActive}
                stepCompleted={!!userDetails?.referral_source}
                enabled={
                  incompleteData?.pc_second_phase_steps.additional_info ===
                  "completed"
                }
              />
            ),
            content: (
              <AccordionContent>
                {activeIndex === 11 ? <div ref={trialref} /> : <div />}
                <ReferralSource
                  setActiveIndex={setActiveIndex}
                  visitId={incompleteData?.id}
                  nextIndex={12}
                  onLeaveReferralStep={() => {
                    pcReferralPanelLeftRef.current = true;
                  }}
                />
              </AccordionContent>
            ),
          },
        ]
      : []),
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Please acknowledge that you have read and accept our code of conduct."
          isActive={isActive}
          stepCompleted={incompleteData?.accepted_tnc}
          enabled={
            incompleteData?.pc_second_phase_steps.additional_info ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === (shouldShowReferralSourceStep ? 12 : 11) ? (
            <div ref={trialref} />
          ) : (
            <div />
          )}
          <CodeOfConduct
            enabled={
              incompleteData?.pc_second_phase_steps.additional_info ===
              "completed"
            }
            incompleteData={incompleteData}
            userDetails={currentPatient}
            termsAccepted={incompleteData?.accepted_tnc}
            setActiveIndex={setActiveIndex}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Enter your payment details"
          isActive={isActive}
          stepCompleted={incompleteData?.meta?.submitted_payment_info}
          enabled={incompleteData?.accepted_tnc}
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === (shouldShowReferralSourceStep ? 13 : 12) ? (
            <div ref={trialref} />
          ) : (
            <div />
          )}
          {incompleteData?.accepted_tnc ? (
            <PaymentDetails
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
            />
          ) : (
            <div />
          )}
        </AccordionContent>
      ),
    },
  ];

  const femaleAccordionData = [
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Patient"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_patient ===
            "completed"
          }
          enabled={true}
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 0 ? <div ref={trialref} /> : <div />}
          <SelectPatient
            incompleteData={incompleteData}
            fetchingPatients={fetchingPatients}
            fetchingProfileDetails={gettingProfileDetails}
            createNewVisit={createNewVisit}
            creatingVisit={creatingVisit}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Condition"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_condition ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_patient ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 1 ? <div ref={trialref} /> : <div />}
          <SelectCondition
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            selected={selectedCondition}
            setSelected={setSelectedCondition}
            gettingConcerns={gettingConcerns}
            visitConcernsPayload={
              visitConcernsPayload?.visit_converns?.map((el) => el?.title) ?? []
            }
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Select Date & Time Slot"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.select_slot === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_condition ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 2 ? <div ref={trialref} /> : <div />}
          {history?.location?.state?.timeslotId ? (
            <SelectSlot
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
              gettingBookingSlots={gettingBookingSlots}
              slotsResponse={slotsResponse}
              preselectedSlotId={history?.location?.state?.timeslotId}
            />
          ) : (
            <SelectSlot
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
              gettingBookingSlots={gettingBookingSlots}
              slotsResponse={slotsResponse}
            />
          )}
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Enter demographics"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.confirm_residence ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_slot === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 3 ? <div ref={trialref} /> : <div />}
          <BasicDetails
            incompleteData={incompleteData}
            basicDetails={currentPatient}
            refetch={getProfileDetails}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            zipDetails={zipDetails}
            setUpdatedZip={setUpdatedZip}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Confirm your identity"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.identity_confirmation ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.confirm_residence ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 4 ? <div ref={trialref} /> : <div />}
          <IdentityConfirmation
            alertDesktopPatient={alertDesktopPatient}
            firstTimeRef={idRef}
            imagePickerRef={imagePickerRef}
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            imageData={imageData}
            setImageData={setImageData}
            successImageTracker={successImageTracker}
            reportImageUploadFailure={reportImageUploadFailure}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Add your preferred pharmacy"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.select_pharmacy ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.identity_confirmation ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 5 ? <div ref={trialref} /> : <div />}
          <SelectPharmacy
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            selectedPharmacy={selectedPharmacy}
            setSelectedPharmacy={setSelectedPharmacy}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List all symptoms from the past 2 weeks"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.symptoms === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.select_pharmacy ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 6 ? <div ref={trialref} /> : <div />}
          <Symptoms
            basicDetails={currentPatient}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Social History"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps?.social_history ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.symptoms === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 7 ? <div ref={trialref} /> : <div />}
          <SocialHistory
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            currentPatient={currentPatient}
            firstAnswer={firstAnswer}
            setFirstAnswer={setFirstAnswer}
            secondAnswer={secondAnswer}
            setSecondAnswer={setSecondAnswer}
            thirdAnswer={thirdAnswer}
            setThirdAnswer={setThirdAnswer}
            thirdYesAnswer={thirdYesAnswer}
            setThirdYesAnswer={setThirdYesAnswer}
            fourthAnswer={fourthAnswer}
            setFourthAnswer={setFourthAnswer}
            fourthYesAnswer={fourthYesAnswer}
            setFourthYesAnswer={setFourthYesAnswer}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List any allergies"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.allergies === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps?.social_history ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 8 ? <div ref={trialref} /> : <div />}
          <Allergies
            currentPatient={currentPatient}
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            selected={shareAllergy}
            setSelected={setShareAllergy}
            selectedAllergies={selectedAllergies}
            setSelectedAllergies={setSelectedAllergies}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="List any medications you are currently taking and/or review your current medication list."
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.medications === "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.allergies === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 9 ? <div ref={trialref} /> : <div />}
          <Medications
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
            selected={shareMeds}
            setSelected={setShareMeds}
            selectedMedications={selectedMedications}
            setSelectedMedications={setSelectedMedications}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Additional medical conditions"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.female_medical_history ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.medications === "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 10 ? <div ref={trialref} /> : <div />}
          <FemaleMedicalHistory
            incompleteData={incompleteData}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Record/Write any additional information you would like your doctor to know"
          isActive={isActive}
          stepCompleted={
            incompleteData?.pc_second_phase_steps.additional_info ===
            "completed"
          }
          enabled={
            incompleteData?.pc_second_phase_steps.female_medical_history ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === 11 ? <div ref={trialref} /> : <div />}
          <AdditionalInfo
            userDetails={currentPatient}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
            setActiveIndex={setActiveIndex}
          />
        </AccordionContent>
      ),
    },
    ...(shouldShowReferralSourceStep
      ? [
          {
            header: ({ isActive }) => (
              <AccordionHeader
                headerTitle="How did you hear about us?"
                isActive={isActive}
                stepCompleted={!!userDetails?.referral_source}
                enabled={
                  incompleteData?.pc_second_phase_steps.additional_info ===
                  "completed"
                }
              />
            ),
            content: (
              <AccordionContent>
                {activeIndex === 12 ? <div ref={trialref} /> : <div />}
                <ReferralSource
                  setActiveIndex={setActiveIndex}
                  visitId={incompleteData?.id}
                  nextIndex={13}
                  onLeaveReferralStep={() => {
                    pcReferralPanelLeftRef.current = true;
                  }}
                />
              </AccordionContent>
            ),
          },
        ]
      : []),
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Please acknowledge that you have read and accept our code of conduct."
          isActive={isActive}
          stepCompleted={incompleteData?.accepted_tnc}
          enabled={
            incompleteData?.pc_second_phase_steps.additional_info ===
            "completed"
          }
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === (shouldShowReferralSourceStep ? 13 : 12) ? (
            <div ref={trialref} />
          ) : (
            <div />
          )}
          <CodeOfConduct
            enabled={
              incompleteData?.pc_second_phase_steps.additional_info ===
              "completed"
            }
            incompleteData={incompleteData}
            userDetails={currentPatient}
            termsAccepted={incompleteData?.accepted_tnc}
            setActiveIndex={setActiveIndex}
            updateVisit={updateVisit}
            updatingVisit={updatingVisit}
          />
        </AccordionContent>
      ),
    },
    {
      header: ({ isActive }) => (
        <AccordionHeader
          headerTitle="Enter your payment details"
          isActive={isActive}
          stepCompleted={incompleteData?.meta?.submitted_payment_info}
          enabled={incompleteData?.accepted_tnc}
        />
      ),
      content: (
        <AccordionContent>
          {activeIndex === (shouldShowReferralSourceStep ? 14 : 13) ? (
            <div ref={trialref} />
          ) : (
            <div />
          )}
          {incompleteData?.accepted_tnc ? (
            <PaymentDetails
              incompleteData={incompleteData}
              updateVisit={updateVisit}
              updatingVisit={updatingVisit}
              setActiveIndex={setActiveIndex}
              checkingInsuranceInNetwork={checkingInsuranceInNetwork}
              insuranceInNetwork={insuranceInNetwork}
            />
          ) : (
            <div />
          )}
        </AccordionContent>
      ),
    },
  ];

  const confirmDeleteVisit = useCallback(async () => {
    try {
      const response = await callDiscardVisit(incompleteData?.id);
      if (response?.error === false) {
        dispatch(primaryCareVisitFlowReducers.reset());
        dispatch(
          setUserDetails({
            userDetails: { ...userDetails, incomplete_visit_id: null },
          })
        );
        history.goBack();
      }
    } catch (error) {}
  }, [callDiscardVisit, dispatch, history, incompleteData?.id, userDetails]);

  const deleteVisit = () => {
    setShowModal({
      state: true,
      children: (_, onClose) => (
        <Alert
          heading="Delete visit"
          message={"Are you sure you want to delete your visit?"}
          primaryButtonText="Confirm"
          onSecondaryButtonClick={onClose}
          onPrimaryButtonClick={() => {
            onClose();
            confirmDeleteVisit();
          }}
        />
      ),
      data: { preventRender: true },
    });
  };

  if (discardingVisit) {
    return (
      <LoaderComponent
        showLoader={{
          state: true,
        }}
      />
    );
  }

  return (
    <div className="h-screen bg-veryLightBlue">
      <Header
        title="Primary Care Visit Details"
        rightActionView={
          incompleteData && Object.keys(incompleteData)?.length ? (
            <p
              onClick={deleteVisit}
              className="px-6 py-4 text-xl font-medium rounded-md cursor-pointer text-red bg-red bg-opacity-10"
            >
              Delete
            </p>
          ) : null
        }
      />
      <div className="overflow-y-auto wrapper h-eliminateHeaderWithFooter">
        <div className="min-h-full p-12 bg-blue">
          <MedicalAssistant
            content={
              <p>
                Hi
                {incompleteData?.patient_first_name
                  ? ` ${incompleteData?.patient_first_name}`
                  : userDetails?.first_name
                  ? ` ${userDetails?.first_name}`
                  : null}
                , let's get started!
                <br />
                <br />
                Please complete the following steps so that our doctor will have
                all the required information before your visit.
              </p>
            }
          />
          <Spacer height={20} />
          {revisedInsuranceDetails?.revised_insurance_line_items
            ?.price_change_type === "revised" &&
          !revisedInsuranceDetails?.revised_insurance_line_items
            ?.price_change_confirmed_at ? (
            <RevisedPaymentBlock
              visitDetails={incompleteData}
              practiceInsurance={revisedInsuranceDetails?.practice_insurance}
              revisedInsuranceDetails={
                revisedInsuranceDetails?.revised_insurance_line_items
              }
            />
          ) : (
            <div />
          )}
          <Spacer height={20} />
          {incompleteData?.failed_images_names?.length > 0 && (
            <div
              ref={failedImagesRef}
              className="space-y-4 mx-0 my-4 p-4 w-full rounded-md bg-yellow-200"
            >
              <p className="font-bold text-2xl flex items-center gap-3">
                <IoAlertCircleOutline /> OOPS! Your photos didn't upload. Select
                link(s) below to re-upload images again to submit your visit.
              </p>
              {incompleteData?.failed_images_names?.includes(
                "identity_confirmation"
              ) ? (
                <div className="flex flex-col">
                  <input
                    ref={imagePickerRef}
                    id="confirm_identity_image_picker"
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    onChange={handleIdImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="confirm_identity_image_picker"
                    className="py-2 flex justify-between items-center cursor-pointer hover:underline"
                  >
                    <p className="text-xl font-semibold">
                      {incompleteData?.failed_images_names?.length}.{" "}
                      {startCase("identity_confirmation")}
                    </p>
                    <p class="font-semibold">
                      {idImageData ? "Completed" : "Click here"}
                    </p>
                  </label>
                  <Button
                    textSize="text-2xl"
                    className="px-4 py-2"
                    disabled={!idImageData}
                    loading={idProofLoading}
                    onClick={handleIdImageSubmit}
                    label="Submit ID Proof"
                  />
                </div>
              ) : null}
            </div>
          )}

          <Spacer height={20} />
          <h3 className="font-black text-2xl pb-4 tracking-wide">
            YOUR VISIT WITH
          </h3>
          {incompleteData?.provider_id ? (
            <div className="p-5 bg-white rounded-4xl border-1/4 border-borderColor">
              <DoctorCard
                imageUrl={incompleteData?.provider_photo_url}
                name={incompleteData?.provider_name}
                speciality="Family Practice: Primary Care Physician"
                isActive={true}
                license={incompleteData?.license}
              />
            </div>
          ) : (
            <div />
          )}
          {incompleteData?.messages.length > 0 ? (
            <>
              <Spacer height={20} />
              <div
                onClick={openChat}
                className="flex items-center p-6 bg-white border cursor-pointer rounded-4xl border-borderColor"
              >
                <img
                  className="w-24 h-24"
                  src={Images.messageIcon}
                  alt="start_visit"
                />
                <div className="ml-6">
                  <h2 className="text-3xl font-bold">Messages</h2>
                  <p className="mt-2 text-2xl font-light leading-8 text-cloudyBlue">
                    {incompleteData?.unread_provider_messages > 0
                      ? `${
                          incompleteData?.unread_provider_messages
                        } unread message${
                          incompleteData?.unread_provider_messages === 1
                            ? ""
                            : "s"
                        }`
                      : "No new messages"}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div />
          )}
          <Spacer height={20} />
          <Accordion
            activeIndexFromData={activeIndex}
            accordionContainerClassName="mb-5 rounded-4xl"
            containerClassName="border-none"
            data={
              currentPatient?.gender === "female"
                ? femaleAccordionData
                : maleAccordionData
            }
            disabledIndicesFromData={disabledIndices}
            enableTransition={true}
          />
        </div>
      </div>
      <BottomFixedButton
        disabled={
          !incompleteData?.meta?.submitted_payment_info ||
          !incompleteData?.accepted_tnc
        }
        loading={
          (incompleteData?.meta?.submitted_payment_info &&
            incompleteData?.accepted_tnc &&
            updatingVisit) ||
          bookingSlot
        }
        type="submit"
        label="Submit Visit"
        onClick={showConfirmation}
      />
      {isMobile ? (
        <div
          className="fixed flex items-center justify-center w-20 md:w-24 h-20 md:h-24 border-2 rounded-full cursor-pointer right-8 bottom-40 bg-orange border-orange"
          onClick={scrollToActiveSection}
        >
          <img
            className="w-8 h-8 md:w-10 md:h-10 filter brightness-0 invert"
            src={Images.checkedGreen}
            alt="scroll to active section"
          />
        </div>
      ) : (
        <div
          onClick={scrollToActiveSection}
          className="fixed flex items-center justify-center w-24 h-24 mr-10 rounded-full cursor-pointer right-1/4 bottom-40 bg-orange shadow-d-2xl"
        >
          <img
            className="w-1/3 filter brightness-0 invert"
            src={Images.checkedGreen}
            alt="scroll to active section"
          />
        </div>
      )}
    </div>
  );
}

export default PrimaryCareCompleteVisitDetails;
